import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import add from 'ember-math-helpers/helpers/add';
import sub from 'ember-math-helpers/helpers/sub';
import { and, not } from 'ember-truth-helpers';
import SignInKioskDemo from 'garaje/components/homepage/kiosk-demo/sign-in-demo';
import OnboardingStepperStepIndicator from 'garaje/components/onboarding-stepper/step-indicator';
import UiCard from 'garaje/components/ui/card';
import DropdownMenu from 'garaje/components/ui/classic/dropdown-menu';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type SetupGuideItemModel from 'garaje/models/setup-guide-item';
import type ChameleonService from 'garaje/services/chameleon';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ImpressionsService from 'garaje/services/impressions';
import type SetupGuideStepperService from 'garaje/services/setup-guide-stepper';
import type StatsigService from 'garaje/services/statsig';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { APP } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { or } from 'macro-decorators';
import { filterBy } from 'macro-decorators';

export default class SetupGuideSteps extends Component {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare statsig: StatsigService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare impressions: ImpressionsService;
  @service declare chameleon: ChameleonService;
  @service('setup-guide-stepper') declare setupGuideStepperService: SetupGuideStepperService;

  @tracked hasSteps = false;
  @tracked showModal = false;
  @tracked totalSteps: number | null = null;
  @tracked currentStepNumber: number | null = null;
  @tracked currentStep?: SetupGuideItemModel | null = null;
  @tracked setupGuideStepperContinued: string | null = null;
  @tracked shouldShowIpadDemo = false;
  @tracked stepperClosed = false;
  @tracked isOpen = false;
  @tracked showStepper = false;

  @or('currentAdmin.isGlobalAdmin', 'currentAdmin.isLocationAdmin') isAdmin!: boolean;
  @filterBy('setupGuideItems', 'completed', true) completedSteps!: SetupGuideItemModel[];
  @filterBy('setupGuideItems', 'completed', false) remainingSteps!: SetupGuideItemModel[];

  constructor(owner: unknown, args: Record<string, unknown>) {
    super(owner, args);
    this.showStepper = this.setupGuideStepperService.showStepper;
    this.setupGuideStepperContinued = localStorage.getItem('setupGuideStepperContinued');
    if (this.showStepper) {
      void this.fetchSetupGuideItems().then(() => this.updateCurrentStep());
    }
  }

  get setupGuideItems(): SetupGuideItemModel[] {
    return this.setupGuideStepperService.setupGuideItems;
  }

  get showCompletedMessage(): boolean {
    return this.setupGuideStepperService.allStepsComplete && !this.setupGuideStepperContinued;
  }

  async fetchSetupGuideItems(): Promise<void> {
    await this.setupGuideStepperService.loadSetupStepsTask.perform(APP.VISITORS);
    if (this.setupGuideItems.length) {
      this.hasSteps = true;
    }
    this.totalSteps = this.setupGuideItems.length;
  }

  @action logStepperViewed(): void {
    this.statsig.logEvent('setup-guide-stepper-viewed');
  }

  @action
  updateCurrentStep(newStepNumber?: number | null, fromArrowClick?: boolean): void {
    if (newStepNumber != null) {
      if (newStepNumber >= 0 && newStepNumber < this.totalSteps!) {
        this.currentStepNumber = newStepNumber;
      } else if (newStepNumber < 0) {
        this.currentStepNumber = this.totalSteps! - 1;
      } else if (newStepNumber >= this.totalSteps!) {
        this.currentStepNumber = 0;
      }
    } else {
      const localCurrentStep = localStorage.getItem('setupGuideStep');
      if (localCurrentStep) {
        this.currentStepNumber = parseInt(localCurrentStep);
        if (
          isNaN(this.currentStepNumber) ||
          this.currentStepNumber < 0 ||
          this.currentStepNumber >= this.setupGuideItems.length
        ) {
          this.currentStepNumber = 0;
        }
      } else {
        this.currentStepNumber = 0;
      }
    }
    localStorage.setItem('setupGuideStep', `${this.currentStepNumber}`);
    this.currentStep = this.setupGuideItems.find((_, idx) => idx === this.currentStepNumber);
    if (fromArrowClick) {
      const item = this.currentStep;
      this.statsig.logEvent('setup_guide_stepper_arrow_clicked', item?.identifier, { app: item?.app });
    }
  }

  @action
  handleArrowClicked(newStepNumber: number): void {
    if (this.showCompletedMessage) {
      localStorage.setItem('setupGuideStepperContinued', 'true');
      this.setupGuideStepperContinued = 'true';
      this.updateCurrentStep(0, true);
    } else {
      this.updateCurrentStep(newStepNumber, true);
    }
  }

  @action
  goToNextStep(): void {
    let newStepNumber = (this.currentStepNumber! + 1) % this.totalSteps!;
    while (this.setupGuideItems[newStepNumber]?.completed && newStepNumber !== this.currentStepNumber) {
      newStepNumber = (newStepNumber + 1) % this.totalSteps!;
    }
    this.updateCurrentStep(newStepNumber);
    this.handleActionClicked();
  }

  @action handleActionClicked(isSecondary?: boolean): void {
    if (!this.currentStep) return;

    const item = this.currentStep;
    const tourId = isSecondary ? item.metadata.secondary_button_chmln_tour_id : item.metadata.chmln_tour_id;
    if (tourId && this.featureFlags.isEnabled('growth_show_chmln_tour')) {
      this.chameleon.showTour(tourId);
    }

    this.statsig.logEvent('setup_guide_stepper_action_clicked', item.identifier, {
      app: item.app,
      secondary: isSecondary,
    });
    if (isSecondary) {
      this.router.transitionTo(item.metadata.secondary_button_route!);
    } else if (item.route) {
      this.router.transitionTo(item.route);
    } else if (this.currentStep.metadata.external_route) {
      if (this.featureFlags.isEnabled('growth_ipad_demo')) {
        this.shouldShowIpadDemo = true;
      } else {
        this.showModal = true;
        void this.impressions.postImpression.perform(IMPRESSION_NAMES.NAVATTIC_IPAD_DEMO_VIEWED);
        if (!this.currentStep.completed) {
          void this.toggleCompletionTask.perform(true);
        }
      }
    }
  }

  @action
  handleTextLinkClick(event: MouseEvent): void {
    const target = <HTMLAnchorElement>event.target;
    if (target.tagName === 'A') {
      const item = this.currentStep;
      this.statsig.logEvent('setup_guide_stepper_text_link_clicked', item?.identifier, { app: item?.app });

      if (!target.getAttribute('target')) {
        event.preventDefault();
        const href = target.getAttribute('href');
        this.router.transitionTo(href!);
      }
    }
  }

  @action
  closeModal(): void {
    this.showModal = false;
    if (this.featureFlags.isEnabled('growth_ipad_demo')) {
      this.shouldShowIpadDemo = false;
    }
  }

  @action
  closeStepper(): void {
    this.stepperClosed = true;
    this.statsig.logEvent('setup_guide_stepper_closed', null, { app: this.currentStep?.app });
    void this.setupGuideStepperService.toggleSetupGuideStepsEnabledTask.perform();
  }

  get markButtonLabel(): string | undefined {
    if (this.currentStep?.completed) {
      return this.currentStep.metadata.dashboard_completed_step_label;
    }

    return this.currentStep?.metadata.dashboard_incomplete_step_label || 'Mark as complete';
  }

  get allStepsComplete(): boolean {
    return !this.setupGuideItems.some((item) => !item.completed);
  }

  get shouldShowActions(): boolean {
    return Boolean(this.isAdmin && this.currentStep);
  }

  get showIpadDemo(): boolean {
    if (this.featureFlags.isEnabled('growth_ipad_demo')) {
      return !this.showModal && this.shouldShowIpadDemo;
    }
    return false;
  }

  toggleCompletionTask = task({ drop: true }, async (isLogged: boolean = false) => {
    const setupGuideItem = this.currentStep;
    const completed = !setupGuideItem!.completed;
    const statsigEventLabel = setupGuideItem!.completed ? 'incomplete' : 'complete';

    setupGuideItem!.completed = completed;
    this.isOpen = !completed;

    if (!isLogged) {
      this.statsig.logEvent(
        `setup_guide_stepper_item_mark_as_${statsigEventLabel}_clicked`,
        setupGuideItem?.identifier,
        {
          app: setupGuideItem?.app,
        },
      );
    }

    try {
      await setupGuideItem?.save();
      this.setupGuideStepperService.checkAllStepsComplete();
    } catch (e) {
      setupGuideItem?.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  <template>
    <div>
      {{#if (and (and this.showStepper this.hasSteps) (not this.stepperClosed))}}
        <div data-test-visitors-setup-guide-stepper {{didInsert this.logStepperViewed}}>
          <UiCard class="mb-6 p-6 pt-2 pr-3 min-h-[186px]">
            <div class="flex items-center space-x-6">
              <h1 class="text-xl">Visitors: Quickstart guide</h1>
              <div class="flex flex-1 items-center">
                {{#each this.completedSteps}}
                  <OnboardingStepperStepIndicator @state="completed" class="step-indicator--gem" data-test-step />
                {{/each}}
                {{#each this.remainingSteps}}
                  <OnboardingStepperStepIndicator @state="disabled" class="step-indicator--gem" data-test-step />
                {{/each}}
              </div>
              <div class="text-carbon-50 font-bold">{{this.completedSteps.length}}/{{this.totalSteps}}</div>

              <LinkTo @route="dashboard" class="text-sm text-gem-50 hover:no-underline">
                See all steps
              </LinkTo>

              <button class="text-lg" {{on "click" this.closeStepper}}>
                <img
                  src="/assets/images/close-rounded.svg"
                  alt="close"
                  class="mt-2 p-3 hover:bg-[#f5f5f7] rounded-lg"
                />
              </button>
            </div>

            <div class="flex items-center mt-0 w-full">
              <div class="flex w-full justify-between p-6 border border-solid border-carbon-20 rounded-md">
                {{#if this.showCompletedMessage}}
                  <div class="flex items-start space-x-3">
                    <img src="/assets/images/party-popper.svg" alt="party-popper" class="h-[60px]" />
                    <div class="ml-3 mr-8" style="max-width: 680px;">
                      <h2 class="text-sm m-0">
                        Congrats on setting up the basics!
                      </h2>
                      <p class="text-sm mb-0">
                        Our support team is available if you have any questions or need assistance along the way. Keep
                        up the great work and enjoy your journey with Visitors!
                      </p>
                    </div>
                    <div class="flex h-full items-center ml-4">
                      <button class="btn btn--small btn--brand mr-4" {{on "click" this.closeStepper}}>
                        Finish
                      </button>
                    </div>
                  </div>
                {{else}}
                  <div class="flex items-start space-x-3">
                    {{#if this.currentStep.completed}}
                      <img src="/assets/images/circle-check.svg" alt="circle-check" class="w-[20px] h-[20px]" />
                    {{else}}
                      <img
                        src="/assets/images/circle-check-light-gray.svg"
                        alt="circle-check-light-gray"
                        class="w-[20px] h-[20px]"
                      />
                    {{/if}}
                    <div class="ml-3 mr-8" style="max-width: 680px;">
                      <h2 class="text-sm text-carbon-70 mb-1">
                        {{this.currentStep.title}}
                      </h2>
                      <p class="text-sm mb-0">
                        {{#if this.currentStep.description}}
                          <span {{on "click" this.handleTextLinkClick}}>{{htmlSafe this.currentStep.description}}</span>
                        {{/if}}
                        {{this.currentStep.metadata.dashboard_description_suffix}}
                      </p>
                      {{! Todo: Add logic here to handle lower tier admins }}
                      {{#unless this.isAdmin}}
                        <p class="text-sm mb-0 mt-3">
                          You don't have permission to set this up. Contact your global admin for this step.
                        </p>
                      {{/unless}}
                    </div>
                  </div>

                  {{#if this.shouldShowActions}}
                    <div class="flex items-center ml-4">
                      {{! This is to show secondary button for preview alongside "Go to next step" }}
                      {{#if (and this.currentStep.completed this.currentStep.metadata.external_route)}}
                        <button
                          class="btn btn--small btn--outline mr-4"
                          {{on "click" (fn this.handleActionClicked false)}}
                        >
                          {{this.currentStep.metadata.dashboard_button_label}}
                        </button>
                        {{#unless this.allStepsComplete}}
                          <button class="btn btn--small btn--brand mr-4" {{on "click" this.goToNextStep}}>
                            Next step
                          </button>
                        {{/unless}}
                      {{else if (and this.currentStep.completed (not this.allStepsComplete))}}
                        <button class="btn btn--small btn--brand mr-4" {{on "click" this.goToNextStep}}>
                          Next step
                        </button>
                      {{else}}
                        {{#if this.currentStep.metadata.secondary_button_label}}
                          <button
                            class="btn btn--small btn--outlineFill mr-4"
                            {{on "click" (fn this.handleActionClicked true)}}
                          >
                            {{this.currentStep.metadata.secondary_button_label}}
                          </button>
                        {{/if}}
                        <button
                          class="btn btn--small btn--brand mr-4"
                          {{on "click" (fn this.handleActionClicked false)}}
                          data-test-stepper-external-button
                        >
                          {{this.currentStep.metadata.dashboard_button_label}}
                        </button>
                      {{/if}}
                      {{#if this.markButtonLabel}}
                        <DropdownMenu @right="-41" @top="12" style="text-align: left !important;">
                          <:trigger>
                            <div
                              class="mx-2 font-bold text-xl cursor-pointer"
                              data-test-setup-guide-stepper-more-actions
                            >
                              ···
                            </div>
                          </:trigger>
                          <:content as |itemClass|>
                            <div
                              role="button"
                              class="{{itemClass}} !text-left !text-sm"
                              style="background-color: white !important;"
                              {{on "click" (fn this.toggleCompletionTask.perform false)}}
                              {{! @glint-expect-error }}
                              disabled={{this.toggleCompletionTask.isRunning}}
                              data-test-setup-guide-stepper-action
                            >
                              {{this.markButtonLabel}}
                            </div>
                          </:content>
                        </DropdownMenu>
                      {{/if}}
                    </div>
                  {{/if}}
                {{/if}}
              </div>
              {{#if this.currentStepNumber}}
                <div class="flex space-x-3 p-6">
                  <button
                    class="p-2 border border-solid border-carbon-40 rounded-md w-[36px] h-[36px]"
                    {{on "click" (fn this.handleArrowClicked (sub this.currentStepNumber 1))}}
                  >
                    <img src="/assets/images/carot-left.svg" alt="left carot" />
                  </button>
                  <button
                    class="p-2 border border-solid border-carbon-40 rounded-md w-[36px] h-[36px]"
                    {{on "click" (fn this.handleArrowClicked (add this.currentStepNumber 1))}}
                  >
                    <img src="/assets/images/carot-right.svg" alt="right carot" />
                  </button>
                </div>
              {{/if}}
            </div>
          </UiCard>
          {{#if this.showIpadDemo}}
            <SignInKioskDemo @isDemoInstance={{false}} @demoName="signin" @onExternalClose={{this.closeModal}} />
          {{/if}}
          {{#if this.showModal}}
            <ModalDialog
              @clickOutsideToClose={{true}}
              @translucentOverlay={{true}}
              @onClose={{this.closeModal}}
              @containerClass="!max-w-[1100px] h-full"
            >

              <iframe
                title="external-content"
                class="border-none min-w-full min-h-full"
                src={{this.currentStep.metadata.external_route}}
                allow="clipboard-read; clipboard-write"
              />

              <button
                type="button"
                class="btn btn--small btn--outline btn--cancel absolute right-4 top-4"
                {{on "click" this.closeModal}}
              >
                <span aria-hidden="true">&times;</span>
                <span class="sr-only">Close</span>
              </button>
            </ModalDialog>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
